/**
 * TranscriptView - Display conversation history with auto-scroll
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { Message } from '../lib/types'
import { formatRole, formatTimestamp, groupMessagesByRole, scrollToBottom, shouldAutoScroll } from '../lib/utils'

interface TranscriptViewProps {
  messages: Message[]
  showTimestamps?: boolean
  groupMessages?: boolean
  className?: string
  maxHeight?: string | number
  emptyStateMessage?: string
  onMessageClick?: (message: Message) => void
}

export const TranscriptView: React.FC<TranscriptViewProps> = ({
  messages,
  showTimestamps = true,
  groupMessages = true,
  className = '',
  emptyStateMessage = 'Conversation will appear here...',
  onMessageClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)

  // Group messages by role if enabled
  const displayMessages = groupMessages ? groupMessagesByRole(messages).flat() : messages

  // Handle auto-scroll
  useEffect(() => {
    if (containerRef.current && autoScroll) {
      scrollToBottom(containerRef.current, true)
    }
  }, [messages, autoScroll])

  // Monitor scroll position
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return

    const shouldScroll = shouldAutoScroll(containerRef.current)
    setAutoScroll(shouldScroll)
    setShowScrollButton(!shouldScroll && messages.length > 5)
  }, [messages.length])

  // Scroll to bottom button handler
  const handleScrollToBottom = useCallback(() => {
    if (containerRef.current) {
      scrollToBottom(containerRef.current, true)
      setAutoScroll(true)
      setShowScrollButton(false)
    }
  }, [])

  return (
    <div className={`tw-relative tw-flex tw-h-full tw-flex-col ${className}`}>
      <div ref={containerRef} className='voice-agent-transcript tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-px-3 tw-py-4' onScroll={handleScroll}>
        {messages.length === 0 ? (
          <div className='tw-px-4 tw-py-8 tw-text-center tw-text-sm tw-text-gray-400'>{emptyStateMessage}</div>
        ) : (
          <>
            {displayMessages.map((message, index) => (
              <MessageItem
                key={message.id}
                message={message}
                showTimestamp={showTimestamps}
                isFirst={index === 0 || displayMessages[index - 1].role !== message.role}
                isLast={index === displayMessages.length - 1 || displayMessages[index + 1]?.role !== message.role}
                onClick={() => onMessageClick?.(message)}
              />
            ))}
          </>
        )}
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={handleScrollToBottom}
          className='tw-absolute tw-bottom-4 tw-right-4 tw-z-10 tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-purple-600 tw-text-white tw-shadow-md tw-transition-all tw-duration-200 hover:tw-scale-105'
          title='Scroll to bottom'
        >
          ↓
        </button>
      )}
    </div>
  )
}

// Individual message item component
const MessageItem: React.FC<{
  message: Message
  showTimestamp: boolean
  isFirst: boolean
  isLast: boolean
  onClick?: () => void
}> = ({ message, showTimestamp, isFirst, onClick }) => {
  const isUser = message.role === 'user'

  return (
    <div className={`tw-mb-2 tw-flex tw-flex-col tw-gap-0.5 ${onClick ? 'tw-cursor-pointer' : ''}`} onClick={onClick}>
      {/* Name (only show on first message) */}
      {isFirst && (
        <div className={`tw-mb-0.5 tw-mt-2 tw-flex tw-items-center tw-text-sm tw-font-medium tw-text-gray-600 ${isUser ? 'tw-justify-end' : ''}`}>
          <strong>{formatRole(message.role)}</strong>
          {showTimestamp && (
            <span className='voice-agent-message-timestamp tw-ml-auto tw-font-mono tw-text-xs tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100'>{formatTimestamp(message.timestamp)}</span>
          )}
        </div>
      )}

      {/* Message content */}
      <div className={`tw-max-w-[80%] tw-break-words tw-rounded-[2px] tw-p-2 tw-text-sm tw-leading-relaxed ${isUser ? 'tw-ml-auto tw-bg-gray-300' : 'tw-mr-auto'}`}>
        {message.content}

        {/* Tool call indicator */}
        {message.metadata?.toolCall && <div className='tw-mt-1 tw-text-xs tw-italic tw-text-gray-500'>🔧 {message.metadata.toolCall}</div>}

        {/* Error indicator */}
        {message.metadata?.error && <div className='tw-mt-1 tw-text-xs tw-text-red-500'>⚠️ {message.metadata.error}</div>}
      </div>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .voice-agent-message:hover .voice-agent-message-timestamp {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  )
}

// Simplified transcript for inline display
export const CompactTranscript: React.FC<{
  messages: Message[]
  maxMessages?: number
}> = ({ messages, maxMessages = 5 }) => {
  // Show only the last N messages
  const recentMessages = messages.slice(-maxMessages)

  return (
    <div
      className='voice-agent-compact-transcript'
      style={{
        fontSize: '12px',
        lineHeight: '1.4',
      }}
    >
      {recentMessages.map((message) => (
        <div
          key={message.id}
          style={{
            marginBottom: '4px',
            opacity: 0.8,
          }}
        >
          <strong>{formatRole(message.role)}:</strong> {message.content.substring(0, 100)}
          {message.content.length > 100 && '...'}
        </div>
      ))}
    </div>
  )
}

export default TranscriptView
